import moment from "moment";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Typography,
  Tooltip,
} from "@material-ui/core";
import { Floor } from "../../floor/floor.model";
import { floorService } from "../../../_services/floor.service";
import { resourceTypeService } from "../../../_services/resourcetype.service";
import { Booking } from "../booking.model";
import { ResourceType } from "../../resource/resourceType.model";
import { Paginated } from "../../commons/Paginated";
import { Resource } from "../../resource/resource.model";
import { bookingService } from "../../../_services/booking.service";
import { useErrorHandler } from "../../errors/ErrorBoundary";
import useSnackBars from "../../commons/snackbar/SnackbarHook";
import { useCurrentBranch } from "../../commons/currentbranch/CurrentBranchHook";
import useCancelToken from "../../../hooks/useCancelToken";
import useBookingCardStyles from "../../../hooks/styles/bookingCardStyles";
import { Cancel } from "@material-ui/icons";
import clsx from "clsx";
import Map from "./Map";
import { BranchReference } from "../../branches/branch.reference.model";
import { useBranches } from "../../commons/branches/BranchesHook";
import { Branch } from "../../branches/branch.model";
import { Amenity } from "../../amenities/amenity.model";
import { amenitiesService } from "../../../_services/amenities.service";
import ResourceFiltersBar from "../../commons/filters/ResourceFiltersBar";
import { BranchInfo, branchService } from "../../../_services/branch.service";

type Props = {
  booking: Booking;
  onResourceSelected: (resource: Resource) => void;
  onCancel: () => void;
};

const BookingMap: React.FC<Props> = ({
  booking,
  onResourceSelected,
  onCancel,
}) => {
  const classes = useBookingCardStyles();
  const [resources, setResources] = useState<Resource[]>(null);
  const [floors, setFloors] = useState<Paginated<Floor>>(null);
  const [floor, setFloor] = useState<Floor>();
  const [types, setTypes] = React.useState<ResourceType[]>([]);
  const [type, setType] = React.useState<ResourceType>(null);
  const [branch, setBranch] = React.useState<Branch>(null);
  const [branchInfo, setBranchInfo] = useState<BranchInfo>(null);
  const [extras, setExtras] = useState<Amenity[]>([]);
  const [extra, setExtra] = useState<Amenity>();
  const [query, setQuery] = React.useState<string>("");
  const {
    currentBranch,
    favouriteFloor,
    onUpdateBranch,
    onUpdateFavouriteFloor,
  } = useCurrentBranch();
  const { branches } = useBranches();
  const { showSuccess, showError, showInfo } = useSnackBars();
  const handleError = useErrorHandler();
  const { getCancelToken, isCancel } = useCancelToken();

  const handleApiError = useCallback(
    (errors: any) => {
      Object.keys(errors).forEach((field) => {
        const e = errors[field];
        showError(e.msg);
      });
    },
    [showError],
  );

  const fetchBranchInfoData = useCallback(async () => {
    try {
      const branchInfo = await branchService.branchInfo();
      setBranchInfo(branchInfo);
    } catch (error) {
      if (!isCancel(error)) {
        error instanceof Error
          ? handleError(error)
          : handleApiError(error.errors);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setResources(null);
      const cancelToken = getCancelToken();
      const _floors = await floorService.list(
        "",
        0,
        Number.MAX_SAFE_INTEGER,
        cancelToken,
      );
      const _types = await resourceTypeService.availableList(cancelToken);
      const _amenities = await amenitiesService.list("", cancelToken);

      let defaultFloor = _floors.docs.find((f) => f.image, null);
      if (favouriteFloor) {
        const favFloor = _floors.docs.find((f) => f.id === favouriteFloor);
        if (favFloor && favFloor.image) {
          defaultFloor = favFloor;
        }
      }

      setFloors(_floors);
      setFloor(defaultFloor);
      setTypes(_types);
      setExtras(_amenities);
      if (!defaultFloor) {
        showInfo(
          `La sucursal seleccionada no tiene planos de oficina configurados, por favor contacte con el administrador.`,
          () => {},
        );
      }
    } catch (error) {
      if (!isCancel(error)) {
        error instanceof Error
          ? handleError(error)
          : handleApiError(error.errors);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch]);

  useEffect(() => {
    fetchBranchInfoData();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch, fetchData]);

  const fetchResourceTypes = useCallback(async () => {
    try {
      const cancelToken = getCancelToken();
      const _types = await resourceTypeService.availableList(cancelToken);
      setTypes(_types);
    } catch (error) {
      if (!isCancel(error)) {
        error instanceof Error
          ? handleError(error)
          : handleApiError(error.errors);
      }
    }
  }, [getCancelToken, handleApiError, handleError, isCancel]);

  useEffect(() => {
    fetchResourceTypes();
  }, [fetchResourceTypes]);

  const fetchFreeResources = useCallback(async () => {
    try {
      if (!booking) {
        return;
      }
      const cancelToken = getCancelToken();
      const _resources = await bookingService.free(
        booking,
        query,
        floor?.id,
        type?.id,
        extra?.id,
        cancelToken,
      );
      setResources(_resources);
    } catch (error) {
      if (!isCancel(error)) {
        error instanceof Error
          ? handleError(error)
          : handleApiError(error.errors);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floor, type, query, booking, extra]);

  useEffect(() => {
    if (!floor) {
      return;
    }
    fetchFreeResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFreeResources]);

  useEffect(() => {
    const branch = branches.find(
      (branch: BranchReference) => branch.id === currentBranch,
    );
    setBranch(branch);
  }, [currentBranch, branches]);

  // Filter resource types based on available resources with current filters
  // const filteredTypes = useMemo(() => {
  //   if (!branchInfo || !types) {
  //     return types;
  //   }

  //   const validTypeIds = new Set<string>();

  //   // Get the relevant floor(s) to check
  //   const floorsToCheck = floor
  //     ? [branchInfo[floor.id]]
  //     : Object.values(branchInfo);

  //   floorsToCheck.forEach((floorData) => {
  //     if (!floorData) return;

  //     floorData.resourceTypes?.forEach((rt) => {
  //       // If amenity filter is selected, check if this resource type has it
  //       if (extra) {
  //         const hasAmenity = rt.amenities?.some(
  //           (a) => a.amenityId === extra.id,
  //         );
  //         if (hasAmenity) {
  //           validTypeIds.add(rt.resourceTypeId);
  //         }
  //       } else {
  //         // No amenity filter, add all resource types
  //         validTypeIds.add(rt.resourceTypeId);
  //       }
  //     });
  //   });

  //   // Return only types that have valid resources
  //   return types.filter((t) => validTypeIds.has(t.id));
  // }, [branchInfo, types, floor, extra]);

  // // Filter amenities based on available resources with current filters
  // const filteredExtras = useMemo(() => {
  //   if (!branchInfo || !extras) {
  //     return extras;
  //   }

  //   const validAmenityIds = new Set<string>();

  //   // Get the relevant floor(s) to check
  //   const floorsToCheck = floor
  //     ? [branchInfo[floor.id]]
  //     : Object.values(branchInfo);

  //   floorsToCheck.forEach((floorData) => {
  //     if (!floorData) return;

  //     // If resource type is selected, only look at that resource type's amenities
  //     if (type) {
  //       const resourceType = floorData.resourceTypes?.find(
  //         (rt) => rt.resourceTypeId === type.id,
  //       );
  //       if (resourceType) {
  //         resourceType.amenities?.forEach((amenity) => {
  //           validAmenityIds.add(amenity.amenityId);
  //         });
  //       }
  //     } else {
  //       // No resource type filter, show all amenities available in the floor
  //       floorData.amenities?.forEach((amenity) => {
  //         validAmenityIds.add(amenity.amenityId);
  //       });
  //     }
  //   });

  //   // Return only amenities that exist in valid resources
  //   return extras.filter((e) => validAmenityIds.has(e.id));
  // }, [branchInfo, extras, floor, type]);

  // Reset resource type if it's no longer valid after filters change
  // useEffect(() => {
  //   if (type && !filteredTypes.some((t) => t.id === type.id)) {
  //     setType(null);
  //   }
  // }, [filteredTypes, type]);

  // // Reset amenity if it's no longer valid after filters change
  // useEffect(() => {
  //   if (extra && !filteredExtras.some((e) => e.id === extra.id)) {
  //     setExtra(null);
  //   }
  // }, [filteredExtras, extra]);

  const handleResourceSelected = (resource: Resource) => {
    onResourceSelected(resource);
  };

  const handleResourceTypeChange = useCallback((type: ResourceType) => {
    setType(type);
  }, []);

  const handleFloorChange = useCallback((floor: Floor) => {
    setResources(null);
    setFloor(floor);
  }, []);

  const handleBranchChange = useCallback((branch: BranchReference) => {
    onUpdateBranch(branch.id);
  }, []);

  const handleExtraChange = useCallback((extra: Amenity) => {
    setExtra(extra);
  }, []);

  const handleNameSearch = (nameQuery: string) => {
    setQuery(nameQuery);
  };

  const handleFloorFavotired = async (floorId: string) => {
    try {
      onUpdateFavouriteFloor(floorId);
      showSuccess("Piso favorito actualizado");
    } catch (error) {
      error instanceof Error
        ? handleError(error)
        : handleApiError(error.errors);
    }
  };

  return (
    <>
      <Box bgcolor="grey.300" position="relative" height="100%" width="100%">
        {/* Float map filter bar */}
        <Box bgcolor="grey.50" padding={1} position="absolute" top={0} left={0}>
          <ResourceFiltersBar
            types={types}
            type={type}
            floors={floors}
            floor={floor}
            favoriteFloorId={favouriteFloor}
            branches={branches}
            branch={branch}
            branchInfo={branchInfo}
            extras={extras || []}
            extra={extra}
            onNameSearch={handleNameSearch}
            onResourceTypeChange={handleResourceTypeChange}
            onFloorChange={handleFloorChange}
            onBranchChange={handleBranchChange}
            onExtraChange={handleExtraChange}
            onFloorFavotired={handleFloorFavotired}
            smallSize={true}
            hideLabels={true}
          />
        </Box>
        {/* Float booking data */}
        {resources && (
          <>
            <Box position="absolute" bottom={0} right={16}>
              <Card className={classes.root}>
                <CardContent className={classes.content}>
                  <Typography className={classes.resource} color="textPrimary">
                    <strong>Nueva Reserva</strong>
                  </Typography>
                  <Typography className={classes.date} color="textPrimary">
                    {moment(booking.bookingStart).format("dddd DD [de] MMMM")}
                  </Typography>
                  <Typography className={classes.time} color="textPrimary">
                    {moment(booking.bookingStart).format("HH:mm")} -{" "}
                    {moment(booking.bookingEnd).format("HH:mm [hs.]")}
                  </Typography>
                </CardContent>
                <CardActions
                  className={clsx(classes.actions, classes.actionsTop)}
                >
                  <Tooltip title="Cancelar">
                    <IconButton size="small" onClick={onCancel}>
                      <Cancel />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Box>
            <Map
              onResourceSelected={handleResourceSelected}
              resources={resources}
              floor={floor}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default BookingMap;
